import { useApolloClient } from '@apollo/client'
import { useCallback, useContext, useEffect } from 'react'
import {
    useLoginMutation,
    useLogoutMutation,
    useMeLazyQuery,
    useMyUpcomingPaymentLazyQuery,
    useRegisterMutation,
} from 'types/graphql'
import { LogoutReason } from 'utils/authContext'
import { LocalStorageKeys } from 'utils/localStorage'
import { UserContext } from 'v2/context/user'
import moment from 'moment-timezone'
import { DOB_FORMAT } from '../constants'
import { usePotentialUser } from '../context/potentialUser'
import { s18n } from '../../i18n'
import { OrganizationContext } from '../context/organization'
import { useRouter } from 'next/router'
import { getDemoUserSignUpData, isDemoEnv } from 'utils/demo'

export const useAuth = function useAuth() {
    const [logoutMutation] = useLogoutMutation()
    const [loginMutation, { loading: loginLoading, data: loginResponse }] = useLoginMutation()
    const [registerMutation, { loading: registerLoading, data: registerResponse }] = useRegisterMutation()
    const apolloClient = useApolloClient()
    const { potentialUser } = usePotentialUser()
    const userContext = useContext(UserContext)
    const { authenticatedUser } = userContext
    const { program, organization } = useContext(OrganizationContext)

    const router = useRouter()

    const [fetchMe, { data: meUserData, called: calledMe, loading: loadingMe }] = useMeLazyQuery({
        ssr: false,
        fetchPolicy: 'no-cache',
    })

    const [
        fetchUpcomingPayment,
        { data: myUpcomingPaymentData, called: calledUpcomingPayment, loading: loadingUpcomingPayments },
    ] = useMyUpcomingPaymentLazyQuery()

    useEffect(() => {
        if (authenticatedUser) {
            if (
                !authenticatedUser.isSuperuser &&
                authenticatedUser.organization &&
                program &&
                program.id !== authenticatedUser.potentialUser.program.id
            ) {
                setTimeout(logoutUser, 0)
            }

            if (
                authenticatedUser.isSuperuser &&
                authenticatedUser.organization &&
                organization &&
                organization.id !== authenticatedUser.organization.id
            ) {
                setTimeout(logoutUser, 0)
            }
        }
    }, [authenticatedUser, organization, program])

    useEffect(() => {
        void fetchAuthenticatedUser()
    }, [])

    const authenticateUser = useCallback(async (email, password) => {
        const loginData = await loginMutation({
            variables: {
                email: email,
                password: password,
            },
        })
        localStorage.setItem(LocalStorageKeys.TOKEN_KEY, loginData.data?.tokenAuth?.token)
        localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN_KEY, loginData.data?.tokenAuth?.refreshToken)

        return fetchAuthenticatedUser()
    }, [])

    const isDemo = isDemoEnv()
    const demoData = isDemo ? getDemoUserSignUpData() : undefined

    const registerUser = useCallback(async (values) => {
        const responses = {
            password1: values.password1 ?? demoData?.password,
            password2: values.password2 ?? demoData?.password,
            inviteCode: potentialUser.inviteCode ?? demoData?.inviteCode,
            securityQuestionQuestion: values.securityQuestion ?? demoData?.securityQuestionQuestion,
            securityQuestionAnswer: values.securityQuestionAnswer ?? demoData?.securityQuestionAnswer,
        }
        await registerMutation({
            variables: {
                email: values.email || potentialUser.email,
                language: s18n.language || potentialUser.language,
                phoneNumber: potentialUser.phoneNumber,
                dob: moment(potentialUser.dob).format(DOB_FORMAT),
                program: '1',
                ...responses
            },
        }).then((response) => {
            if (!response.data.register.errors) {
                return authenticateUser(values.email || potentialUser.email, responses.password1)
            }
        })
    }, [])

    const logoutUser = useCallback(
        async (reason = LogoutReason.General) => {
            try {
                await logoutMutation()
            } catch (ignore) {}
            localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN_KEY)
            localStorage.removeItem(LocalStorageKeys.TOKEN_KEY)

            const extraFragment = router.pathname.includes('admin') ? '/admin' : ''

            const url =
                reason === LogoutReason.Inactivity
                    ? `/v2${
                          window.POTENTIAL_USER?.program?.urlFragment || window.PROGRAM.urlFragment
                      }${extraFragment}/login?inactivity=1`
                    : `/v2${
                          window.POTENTIAL_USER?.program?.urlFragment || window.PROGRAM.urlFragment
                      }${extraFragment}/login`
            if (window.Intercom) {
                window.Intercom('shutdown')
            }
            window.location.href = url
        },
        [meUserData, apolloClient, fetchMe]
    )

    useEffect(() => {
        if (calledMe && !loadingMe && meUserData?.me) {
            s18n.changeLanguage(meUserData?.me.language.toLowerCase() || 'en')
            userContext.setAuthenticatedUser(meUserData?.me)
            const transferMethods = meUserData?.me.transferMethods.edges.map((i) => {
                const clone = JSON.parse(JSON.stringify(i.node))
                delete clone.__typename
                return clone
            })

            userContext.setTransferMethods(transferMethods)
        }
    }, [meUserData, calledMe])

    useEffect(() => {
        if (userContext.transferMethods) {
            void fetchUpcomingPayment()
        }
    }, [fetchUpcomingPayment, userContext.transferMethods])

    useEffect(() => {
        if (myUpcomingPaymentData && !loadingUpcomingPayments) {
            userContext.setUpcomingPayment(myUpcomingPaymentData)
        }
    }, [calledUpcomingPayment, myUpcomingPaymentData, userContext.transferMethods])

    const fetchAuthenticatedUser = useCallback(async () => {
        try {
            void fetchMe()
        } catch (ignore) {}
    }, [fetchMe])

    return {
        fetchAuthenticatedUser,
        calledMe,
        loadingMe,
        registerError: registerResponse?.register.errors || {},
        registerLoading,
        loginLoading,
        loginResponse,
        loginError: loginResponse?.tokenAuth?.errors?.nonFieldErrors[0]?.code,
        registerUser,
        authenticateUser,
        logoutUser,
    }
}
